/**
 * The internal console, as the product.
 *
 * This is the piece the brief's §2 said was missing: the prototype-accurate screens, on the real
 * backend, behind the real session. The components are the same ones the visual acceptance
 * measures against the frozen reference — not a second implementation that looks like them — and
 * everything they read comes from `loadDatabase` and everything they write goes through
 * `createApiCommands`.
 *
 * Three things this is careful about.
 *
 * The session is the product's, not the prototype's. §10: the reference's «doğrudan panele gir»
 * bypass is not carried over, the real password sign-in stays exactly as it is, and the sign out
 * in the sidebar drops the session rather than linking to a screen.
 *
 * The role decides what is OFFERED, never what is allowed. Every command is authorized
 * server-side against the authorization matrix; a viewer who types an owner's URL gets the
 * backend's own 403.
 *
 * The addresses are the prototype's. `#/admin/templates` in the reference is `/admin/templates`
 * here, so the two can be held side by side.
 */
import { type ReactElement, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSession } from '../auth/session';
import { LoginScreen } from '../screens/LoginScreen';
import { ResetPasswordScreen } from '../screens/ResetPasswordScreen';
import { NoRoleAssigned, SessionExpired, UnknownUser } from '../shell/states';
import { Loading } from '../ui';
import { ApiDataProvider, useApiStore } from './data/api-provider';
import { createApiCommands } from './data/commands-api';
import { V07CommandsProvider } from './data/commands-context';
import { withReadInvalidation } from './data/invalidate-reads';
import { createApiQueries } from './data/queries-api';
import { V07QueriesProvider } from './data/queries-context';
import { apiTemplateEmail, TemplateEmailProvider } from './data/template-email';
import { apiTemplateLinkedInCard, TemplateLinkedInCardProvider } from './data/template-linkedin-card';
import { V07RoleProvider } from './data/session';
import { acknowledgeUnusableIntent, clearPendingDraftsOf, clearPendingIntent } from './lib/pending-intent';
import { AssetsScreen } from './screens/AssetsScreen';
import { CredentialDetailScreen } from './screens/CredentialDetailScreen';
import { CredentialsScreen } from './screens/CredentialsScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { clearEditorMemory, EditorScreen } from './screens/EditorScreen';
import { ImportScreen } from './screens/ImportScreen';
import { NewTemplateScreen } from './screens/NewTemplateScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { TemplatesScreen } from './screens/TemplatesScreen';
import { AdminShell } from './shell/AdminShell';
import { ToastProvider as V07ToastProvider } from './ui/toast';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Sahip',
  admin: 'Yönetici',
  operator: 'Operatör',
  viewer: 'İzleyici',
};

/** Two letters from the display name, the way the reference's avatar chip carries them. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toLocaleUpperCase('tr');
}

/**
 * The world, read from the backend and written through the commands.
 *
 * The data provider and the command layer are mounted together because they are two halves of one
 * thing: a command calls the authorized route and then re-reads this store, so what the user sees
 * after a click is what the server holds rather than what the click hoped for.
 */
function V07Data({ children }: { children: ReactNode }): ReactElement {
  const { api } = useSession();
  const { store, state } = useApiStore(api);

  /*
   * The current store, reachable without BEING a dependency.
   *
   * The command layer and the read layer both need to look at whatever the store holds right now, and
   * the obvious spelling — closing over `store` and listing it in the memo — rebuilt both of them on
   * every store write. That mattered: the detail screen's hydration effect depends on the command
   * object, so a new command object was a new hydration, which wrote to the store, which built a new
   * command object. A ref is read at call time and changes nothing about identity.
   */
  const dbRef = useRef(store.db);
  dbRef.current = store.db;
  const readDb = useCallback(() => dbRef.current, []);

  /*
   * A successful command has changed the answers, and no question changed to say so.
   *
   * `useRead` re-reads when its question changes; «the world moved» is not part of a question. So one
   * number stands for it: bumped once per successful world-changing command, folded into every read's
   * identity. Deliberately NOT bumped by a store write — that is how a refresh loop is built.
   */
  const [revision, setRevision] = useState(0);
  const invalidate = useCallback(() => setRevision((n) => n + 1), []);

  const commands = useMemo(
    () =>
      withReadInvalidation(
        createApiCommands({
          api,
          refresh: state.reload,
          read: readDb,
          patchLocal: store.update,
        }),
        invalidate,
      ),
    [api, state.reload, readDb, store.update, invalidate],
  );

  /*
   * The reads that have to be complete go straight to the backend rather than through the store:
   * the register's page and count, the upload history's page, the panel's totals, and one record by
   * the code that was asked for.
   */
  const queries = useMemo(() => createApiQueries(api, readDb), [api, readDb]);

  /* «Bu sertifikayla gönderilecek e-posta»: the same four routes for the wizard and the editor. */
  const templateEmail = useMemo(() => apiTemplateEmail(api), [api]);
  /* «Bu sertifikanın LinkedIn paylaşım görseli»: the same four routes, the same two entrances. */
  const linkedInCard = useMemo(() => apiTemplateLinkedInCard(api), [api]);

  return (
    <ApiDataProvider store={store}>
      <V07CommandsProvider commands={commands}>
        <TemplateEmailProvider value={templateEmail}>
          <TemplateLinkedInCardProvider value={linkedInCard}>
            <V07QueriesProvider queries={queries} revision={revision}>
              {state.error !== null ? (
                <div className="warn-box" role="alert" style={{ margin: '16px' }}>
                  {state.error}
                </div>
              ) : null}
              {/*
               * Nothing is rendered until the FIRST read settles — and everything stays rendered after.
               *
               * The screens are written against a world that is simply THERE, which is true of the
               * prototype and not of a product that has to ask a server. Mounted early they would show
               * an empty register, and worse, an enabled control over data that has not arrived: the
               * first click on «+ Tek Credential» said «önce bir şablon yayınlayın» about a template
               * that was already published and merely not loaded yet.
               *
               * `ready` rather than `!loading`, because every command refreshes the world when it
               * finishes: gating on `loading` tore the screen — and with it the toast host above it —
               * down on every single click, so the message the button had just raised vanished before it
               * could be read. A button whose outcome is invisible is the decorative button §11 forbids
               * just as much as one that does nothing.
               */}
              {state.ready ? (
                children
              ) : (
                <section className="stack" style={{ padding: '24px' }} aria-busy="true">
                  <p className="muted">Yükleniyor…</p>
                </section>
              )}
            </V07QueriesProvider>
          </TemplateLinkedInCardProvider>
        </TemplateEmailProvider>
      </V07CommandsProvider>
    </ApiDataProvider>
  );
}

/**
 * The session gate, in the order the shell's states are decided: still restoring, expired, signed
 * out, MFA outstanding, account not provisioned, no role, ready. It is a routing decision, not an
 * authorization one.
 */
export function RequireV07Session({ children }: { children: ReactNode }): ReactElement {
  const { auth, actor, loading, sessionExpired, unknownUser, role, signOut } = useSession();

  if (loading)
    return (
      <div className="login-wrap">
        <Loading label="Oturum kontrol ediliyor…" />
      </div>
    );
  if (sessionExpired) return <SessionExpired />;
  if (auth.status === 'signed_out') return <LoginScreen />;
  /*
   * Two different answers, told apart by what the server actually said.
   *
   * `unknownUser` is the 401 `unknown_internal_user`: this installation has no internal user for
   * the session's subject, which is the state every account is in before the first administrator
   * is created. Anything else that leaves no actor — including an account with a user record but
   * no active role — is a role problem, and somebody who could assign one demonstrably exists.
   * Neither screen signs anybody out: a missing role is not a reason to destroy a working session,
   * and the first of the two needs that session to finish the setup it offers.
   *
   * There is no assurance-level branch here any more (PO 7.10). The role is the gate, on this
   * screen and — the one that actually decides — on every command the backend authorizes.
   */
  if (unknownUser) return <UnknownUser />;
  if (!actor) return <NoRoleAssigned />;
  if (!actor.activeRole) return <NoRoleAssigned />;

  return (
    <V07RoleProvider
      role={role}
      account={{
        name: actor.displayName || actor.email,
        initials: initialsOf(actor.displayName || actor.email),
        roleLabel: ROLE_LABEL[actor.activeRole] ?? actor.activeRole,
        ownerKey: actor.userId,
        signOut: () => {
          /*
           * Nothing half-finished survives a sign-out, on a shared machine least of all.
           *
           * «Nothing» used to mean the single-issuance intent and nothing else, which left the
           * editor's module-level memory — an unsaved DESIGN — sitting there for whoever signed in
           * next. All of it goes: the intent, the breadcrumb about an intent that could not be
           * used, and the editor's working copy.
           */
          clearPendingIntent();
          acknowledgeUnusableIntent();
          clearPendingDraftsOf(actor.userId);
          clearEditorMemory();
          void signOut();
        },
      }}
    >
      <V07Data>
        {/*
         * The prototype's own toast host, which is what every v0.7 screen raises its outcomes
         * through. It is a different component from the product shell's `src/ui` toast — the
         * reference's markup, at the reference's position — and the screens ask for it by name.
         */}
        <V07ToastProvider>
          {/* No navigation strip sits above the product's shell, so it reserves no room for one. */}
          <AdminShell topStrip={false}>{children}</AdminShell>
        </V07ToastProvider>
      </V07Data>
    </V07RoleProvider>
  );
}

/** One wrapper per route, so a screen is never mounted outside the session it reads through. */
const guarded = (screen: ReactElement): ReactElement => <RequireV07Session>{screen}</RequireV07Session>;

export function V07InternalRoutes(): ReactElement {
  return (
    <Routes>
      {/* The login screen brings its own shell: it is the one admin surface outside `.app`. */}
      <Route path="login" element={<LoginScreen />} />
      {/*
       * Password recovery, outside the session gate for the same reason `login` is: the person
       * following the link cannot sign in, which is the whole point of the link. It is not a way
       * in — the screen renders a form only after the provider has accepted the link's own
       * session, and that session can set a password and nothing else.
       */}
      <Route path="reset-password" element={<ResetPasswordScreen />} />
      <Route index element={guarded(<DashboardScreen />)} />
      <Route path="assets" element={guarded(<AssetsScreen />)} />
      <Route path="templates" element={guarded(<TemplatesScreen />)} />
      <Route path="templates/new" element={guarded(<NewTemplateScreen />)} />
      {/*
       * The editor answers at both spellings. The prototype's own catalogue addresses it as
       * `#/admin/templates/<id>`, and its screens navigate to `…/<id>/editor`; with only the first
       * registered, «Kopyala» created the copy on the server and then fell through to the
       * catch-all, landing the user on the dashboard.
       */}
      <Route path="templates/:templateId" element={guarded(<EditorScreen />)} />
      <Route path="templates/:templateId/editor" element={guarded(<EditorScreen />)} />
      <Route path="import" element={guarded(<ImportScreen />)} />
      <Route path="credentials" element={guarded(<CredentialsScreen />)} />
      <Route path="credentials/:code" element={guarded(<CredentialDetailScreen />)} />
      <Route path="settings" element={<Navigate replace to="/admin/settings/email" />} />
      <Route path="settings/:tab" element={guarded(<SettingsScreen />)} />
      <Route path="*" element={<Navigate replace to="/admin" />} />
    </Routes>
  );
}
